import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, Card } from '@ibsys/kit';
import { RefreshCwIcon, TriangleAlertIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn, hardReload } from '@/shared/lib';

import { CopyButton } from '../CopyButton/CopyButton';

type Props = {
  /** Пойманная ошибка рендера */
  error: Error;
  /** Стек React-компонентов на момент ошибки */
  componentStack: string | null;
  /** Вписать экран в область содержимого каркаса; по умолчанию он занимает всё окно */
  isEmbedded?: boolean;
};

/**
 * Страница фатальной ошибки фронта.
 *
 * Заменяет «сырое» падение приложения дружелюбным сообщением в стиле проекта (цвета и
 * компоненты берутся из текущей темы), кнопкой полной перезагрузки с очисткой кешей и
 * свёрнутым блоком с текстом ошибки, который можно скопировать одной кнопкой.
 *
 * Показывается в двух областях: на всё окно, когда упал сам каркас приложения, и внутри
 * области содержимого (`isEmbedded`), когда упала отдельная страница — тогда левое меню
 * разделов и верхняя панель остаются на месте и пользователь уходит в другой раздел.
 */
export const ErrorPage: FC<Props> = ({ error, componentStack, isEmbedded = false }) => {
  const { t } = useTranslation('error');
  const [isReloading, setIsReloading] = useState(false);

  const errorText = [error.stack ?? error.message, componentStack].filter(Boolean).join('\n\n');

  const handleReload = () => {
    setIsReloading(true);
    void hardReload();
  };

  return (
    <div
      className={cn(
        'flex w-full items-center justify-center bg-background p-4',
        isEmbedded ? 'h-full overflow-auto' : 'min-h-screen',
      )}
    >
      <Card className="flex w-full max-w-lg flex-col gap-6 p-8">
        <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-muted">
          <TriangleAlertIcon className="size-7 text-muted-foreground" />
        </div>

        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-xl font-semibold text-foreground">{t('title')}</h1>
          <p className="text-sm text-muted-foreground">{t('description')}</p>
        </div>

        <Button className="w-full" icon={<RefreshCwIcon />} isLoading={isReloading} onClick={handleReload}>
          {t('reload')}
        </Button>

        <Accordion type="single" collapsible>
          <AccordionItem value="details" className="border-none">
            <AccordionTrigger className="text-sm text-muted-foreground">{t('details')}</AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col gap-2">
                <CopyButton text={errorText} tooltip={t('copy')} className="size-8 self-end" />
                <pre className="max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap text-muted-foreground">
                  {errorText}
                </pre>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>
    </div>
  );
};
